/**
 * FailureAnalysisCard — structured render of testops_diagnose_test findings.
 *
 * Displayed inline in the chat when the agent calls testops_diagnose_test.
 * Read-only; the agent's chat text proposes the actual fix in natural
 * language and (if user agrees) calls testops_edit_test_step / similar,
 * which renders the existing WriteProposalCard with its own approval flow.
 *
 * Sections, in priority order:
 *   1. Header — root-cause pill, confidence, test name
 *   2. Recommended-fix banner — what the diagnosis suggests
 *   3. Infra-issue alert — only when many other tests share the failure
 *   4. Failed step — number, action, error category, screenshot link
 *   5. History — sparkline + KPIs + regression timeline
 *   6. Page change — added/removed buttons & form fields
 *   7. Validated baseline — what learned_knowledge says works on this URL
 *   8. Why this conclusion — bulleted evidence list
 *
 * Tenant scope is enforced server-side; this component just renders what
 * the API returned.
 */
import { useState } from 'react'
import {
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  XCircle,
  RefreshCw,
} from 'lucide-react'
import clsx from 'clsx'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RootCause =
  | 'label_change'
  | 'feature_removed'
  | 'regression_unknown'
  | 'flaky_known'
  | 'infra_issue'
  | 'unclear'

type FixKind =
  | 'edit_step'
  | 'remove_step'
  | 'wait'
  | 'investigate'
  | 'investigate_infra'
  | 'no_action'

export interface DiagnosticFindings {
  test_case: { id: string; name: string }
  current: {
    execution_id: string
    status: string
    started_at: string | null
    completed_at: string | null
    error: string | null
    failed_step: {
      step_number: number
      action: string
      error_category: string | null
      error: string | null
      screenshot_s3_key: string | null
      screenshot_path: string | null
      url: string | null
    } | null
  }
  history: {
    total_runs: number
    passed: number
    failed: number
    success_rate: number | null
    recent_runs: Array<{
      execution_id: string
      status: string
      created_at: string | null
    }>
    regression_started_at: string | null
    last_passing_at: string | null
    current_failure_streak?: number
    flake_score: number | null
  }
  baseline: {
    url: string
    validated_interactions: string[]
    validated_form_fields: string[]
    validated_validations: string[]
  } | null
  page_change: {
    current_snapshot_id: string
    current_captured_at: string
    prior_snapshot_id: string | null
    prior_captured_at?: string
    metadata_similarity_score?: number | null
    visual_similarity_score?: number | null
    changes: {
      added_buttons: string[]
      removed_buttons: string[]
      added_form_fields: string[]
      removed_form_fields: string[]
      added_links?: string[]
      removed_links?: string[]
      added_nav_items?: string[]
      removed_nav_items?: string[]
    } | null
    note?: string
  } | null
  infra_signal: {
    error_category: string | null
    same_category_count_24h: number
    is_likely_infra: boolean
    window_hours: number
  }
  hypothesis: {
    root_cause: RootCause
    confidence: number
    evidence: string[]
    recommended_fix: {
      kind: FixKind
      step_number?: number | null
      new_instruction?: string
      rationale: string
      candidates?: string[]
    }
  }
}

// ---------------------------------------------------------------------------
// Per-cause styling
// ---------------------------------------------------------------------------

interface CauseStyle {
  label: string
  pillClass: string
  icon: typeof AlertTriangle
}

const CAUSE_STYLES: Record<RootCause, CauseStyle> = {
  label_change: {
    label: 'Label change',
    pillClass: 'bg-warning-soft text-warning-foreground border-warning/30',
    icon: AlertTriangle,
  },
  feature_removed: {
    label: 'Feature removed',
    pillClass: 'bg-destructive-soft text-destructive-foreground border-destructive/30',
    icon: XCircle,
  },
  regression_unknown: {
    label: 'Regression',
    pillClass: 'bg-warning-soft text-warning-foreground border-warning/30',
    icon: AlertTriangle,
  },
  flaky_known: {
    label: 'Known flaky',
    pillClass: 'bg-accent-soft text-accent-soft-foreground border-accent/30',
    icon: RefreshCw,
  },
  infra_issue: {
    label: 'Infra / environment',
    pillClass: 'bg-destructive-soft text-destructive-foreground border-destructive/30',
    icon: AlertCircle,
  },
  unclear: {
    label: 'Unclear',
    pillClass: 'bg-foreground/[0.06] text-muted-foreground border-subtle',
    icon: Info,
  },
}

const FIX_LABEL: Record<FixKind, string> = {
  edit_step: 'Edit step',
  remove_step: 'Remove step',
  wait: 'Add wait / longer timeout',
  investigate: 'Investigate further',
  investigate_infra: 'Investigate infrastructure',
  no_action: 'No action',
}

function formatPercent(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return `${Math.round(v * 100)}%`
}

function formatRelativeTimestamp(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const diffMs = Date.now() - d.getTime()
    const diffH = diffMs / (1000 * 60 * 60)
    if (diffH < 1) return 'less than an hour ago'
    if (diffH < 24) return `${Math.round(diffH)}h ago`
    const diffD = diffH / 24
    if (diffD < 14) return `${Math.round(diffD)}d ago`
    return d.toLocaleDateString()
  } catch {
    return iso
  }
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export function FailureAnalysisCard({ findings }: { findings: DiagnosticFindings }) {
  const { test_case, current, history, baseline, page_change, infra_signal, hypothesis } =
    findings
  const cause = CAUSE_STYLES[hypothesis.root_cause] || CAUSE_STYLES.unclear
  const CauseIcon = cause.icon

  const [open, setOpen] = useState({
    failedStep: true,
    history: false,
    pageChange: !!page_change?.changes,
    baseline: false,
    evidence: false,
  })

  const toggle = (k: keyof typeof open) =>
    setOpen((prev) => ({ ...prev, [k]: !prev[k] }))

  return (
    <div className="rounded-lg border border-subtle bg-card overflow-hidden text-sm">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-subtle bg-card/60 px-4 py-3">
        <CauseIcon className="h-4 w-4 text-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground truncate">
            {test_case.name}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            <span
              className={clsx(
                'rounded border px-1.5 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide',
                cause.pillClass,
              )}
            >
              {cause.label}
            </span>
            <ConfidenceBadge value={hypothesis.confidence} />
          </div>
        </div>
      </div>

      {/* Recommended fix banner */}
      <div className="px-4 py-3 bg-foreground/[0.03] border-b border-subtle">
        <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
          Recommended: {FIX_LABEL[hypothesis.recommended_fix.kind] || 'Investigate'}
        </div>
        <div className="text-xs text-foreground leading-relaxed">
          {hypothesis.recommended_fix.rationale}
        </div>
        {hypothesis.recommended_fix.candidates &&
          hypothesis.recommended_fix.candidates.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {hypothesis.recommended_fix.candidates.map((c) => (
                <span
                  key={c}
                  className="text-[0.65rem] px-2 py-0.5 rounded bg-foreground/[0.06] text-foreground"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
      </div>

      {/* Infra alert */}
      {infra_signal.is_likely_infra && (
        <div className="px-4 py-2 bg-destructive-soft border-b border-destructive/30">
          <div className="text-xs text-destructive-foreground flex items-start gap-2">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <div>
              {infra_signal.same_category_count_24h} other tests hit{' '}
              <code className="font-mono text-[0.7rem] px-1 rounded bg-foreground/[0.08]">
                {infra_signal.error_category}
              </code>{' '}
              in the last {infra_signal.window_hours}h. Likely an environment
              issue, not a test bug.
            </div>
          </div>
        </div>
      )}

      {/* Sections */}
      <div className="divide-y divide-subtle">
        {current.failed_step && (
          <Section
            title={`Failed step #${current.failed_step.step_number}`}
            isOpen={open.failedStep}
            onToggle={() => toggle('failedStep')}
          >
            <FailedStepDetail step={current.failed_step} />
          </Section>
        )}

        <Section
          title={`History · ${history.passed}/${history.total_runs} passing${
            history.success_rate !== null
              ? ` (${formatPercent(history.success_rate)})`
              : ''
          }`}
          isOpen={open.history}
          onToggle={() => toggle('history')}
          rightSlot={<RecentRunsSparkline runs={history.recent_runs} />}
        >
          <HistoryDetail history={history} />
        </Section>

        {page_change && (
          <Section
            title="Page snapshot"
            isOpen={open.pageChange}
            onToggle={() => toggle('pageChange')}
          >
            <PageChangeDetail change={page_change} />
          </Section>
        )}

        {baseline && (
          <Section
            title="Validated baseline"
            isOpen={open.baseline}
            onToggle={() => toggle('baseline')}
          >
            <BaselineDetail baseline={baseline} />
          </Section>
        )}

        {hypothesis.evidence.length > 0 && (
          <Section
            title="Why this conclusion"
            isOpen={open.evidence}
            onToggle={() => toggle('evidence')}
          >
            <ul className="space-y-1 text-xs text-foreground">
              {hypothesis.evidence.map((e, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-subtle-foreground shrink-0">•</span>
                  <span className="leading-relaxed">{e}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Section({
  title,
  isOpen,
  onToggle,
  rightSlot,
  children,
}: {
  title: string
  isOpen: boolean
  onToggle: () => void
  rightSlot?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs hover:bg-foreground/[0.04]"
      >
        <span className="font-semibold uppercase tracking-wide text-muted-foreground flex-1 truncate">
          {title}
        </span>
        {rightSlot}
        {isOpen ? (
          <ChevronUp className="h-3 w-3 text-subtle-foreground shrink-0" />
        ) : (
          <ChevronDown className="h-3 w-3 text-subtle-foreground shrink-0" />
        )}
      </button>
      {isOpen && <div className="px-4 pb-3 pt-1">{children}</div>}
    </div>
  )
}

function ConfidenceBadge({ value }: { value: number }) {
  const pct = Math.round(value * 100)
  const tone =
    pct >= 80
      ? 'text-success-foreground'
      : pct >= 50
        ? 'text-foreground'
        : 'text-subtle-foreground'
  return (
    <span className={clsx('text-[0.7rem] font-medium', tone)}>
      Confidence {pct}%
    </span>
  )
}

function FailedStepDetail({
  step,
}: {
  step: NonNullable<DiagnosticFindings['current']['failed_step']>
}) {
  return (
    <div className="space-y-2 text-xs">
      <div className="rounded bg-foreground/[0.04] px-2 py-1.5">
        <div className="text-[0.65rem] uppercase tracking-wide text-subtle-foreground mb-0.5">
          Action
        </div>
        <div className="text-foreground break-words">{step.action || '—'}</div>
      </div>
      {step.error_category && (
        <div className="flex items-center gap-2">
          <span className="text-[0.65rem] uppercase tracking-wide text-subtle-foreground">
            Error category
          </span>
          <code className="text-[0.7rem] font-mono px-1.5 py-0.5 rounded bg-destructive-soft text-destructive-foreground">
            {step.error_category}
          </code>
        </div>
      )}
      {step.error && (
        <div className="rounded bg-destructive-soft/40 border border-destructive/20 px-2 py-1.5">
          <div className="text-[0.65rem] uppercase tracking-wide text-destructive-foreground mb-0.5">
            Error
          </div>
          <pre className="text-[0.7rem] text-destructive-foreground whitespace-pre-wrap break-words">
            {step.error}
          </pre>
        </div>
      )}
      {step.url && (
        <div className="text-[0.7rem] text-muted-foreground">
          <span className="text-subtle-foreground">URL: </span>
          <code className="font-mono break-all">{step.url}</code>
        </div>
      )}
    </div>
  )
}

function RecentRunsSparkline({
  runs,
}: {
  runs: DiagnosticFindings['history']['recent_runs']
}) {
  // Newest-first from the API. Reverse for chronological reading: oldest left, newest right.
  const ordered = [...runs].reverse()
  return (
    <div className="flex items-center gap-0.5">
      {ordered.map((r, i) => (
        <span
          key={i}
          title={`${r.status}${r.created_at ? ` · ${formatRelativeTimestamp(r.created_at)}` : ''}`}
          className={clsx(
            'h-3 w-1.5 rounded-sm',
            r.status === 'completed'
              ? 'bg-success'
              : r.status === 'failed'
                ? 'bg-destructive'
                : 'bg-foreground/[0.15]',
          )}
        />
      ))}
    </div>
  )
}

function HistoryDetail({
  history,
}: {
  history: DiagnosticFindings['history']
}) {
  return (
    <div className="grid grid-cols-2 gap-2 text-xs">
      <Stat label="Success rate" value={formatPercent(history.success_rate)} />
      <Stat label="Total runs (window)" value={String(history.total_runs)} />
      <Stat
        label="Failure streak"
        value={String(history.current_failure_streak ?? 0)}
        tone={
          (history.current_failure_streak ?? 0) > 0 ? 'warning' : 'default'
        }
      />
      <Stat
        label="Flake score"
        value={
          history.flake_score !== null && history.flake_score !== undefined
            ? formatPercent(history.flake_score)
            : '—'
        }
        tone={(history.flake_score ?? 0) >= 0.3 ? 'warning' : 'default'}
      />
      {history.regression_started_at && (
        <Stat
          label="Regression started"
          value={formatRelativeTimestamp(history.regression_started_at)}
          tone="warning"
          fullWidth
        />
      )}
      {history.last_passing_at && (
        <Stat
          label="Last passing run"
          value={formatRelativeTimestamp(history.last_passing_at)}
          tone="success"
          fullWidth
        />
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  tone = 'default',
  fullWidth = false,
}: {
  label: string
  value: string
  tone?: 'default' | 'success' | 'warning'
  fullWidth?: boolean
}) {
  const valueClass =
    tone === 'warning'
      ? 'text-warning-foreground'
      : tone === 'success'
        ? 'text-success-foreground'
        : 'text-foreground'
  return (
    <div
      className={clsx(
        'rounded bg-foreground/[0.04] px-2 py-1.5',
        fullWidth && 'col-span-2',
      )}
    >
      <div className="text-[0.6rem] uppercase tracking-wide text-subtle-foreground">
        {label}
      </div>
      <div className={clsx('text-xs font-medium mt-0.5', valueClass)}>
        {value}
      </div>
    </div>
  )
}

function PageChangeDetail({
  change,
}: {
  change: NonNullable<DiagnosticFindings['page_change']>
}) {
  if (!change.changes) {
    return (
      <div className="text-xs text-subtle-foreground italic">
        {change.note || 'No prior snapshot to compare against.'}
      </div>
    )
  }
  const c = change.changes
  const addedLinks = c.added_links ?? []
  const removedLinks = c.removed_links ?? []
  const addedNav = c.added_nav_items ?? []
  const removedNav = c.removed_nav_items ?? []
  const hasAny =
    c.added_buttons.length ||
    c.removed_buttons.length ||
    c.added_form_fields.length ||
    c.removed_form_fields.length ||
    addedLinks.length ||
    removedLinks.length ||
    addedNav.length ||
    removedNav.length
  if (!hasAny) {
    return (
      <div className="text-xs text-subtle-foreground italic">
        No structural changes between snapshots.
      </div>
    )
  }
  return (
    <div className="space-y-2 text-xs">
      <div className="text-[0.7rem] text-muted-foreground">
        Captured {formatRelativeTimestamp(change.current_captured_at)}
        {change.prior_captured_at && (
          <>
            {' · prior '}
            {formatRelativeTimestamp(change.prior_captured_at)}
          </>
        )}
      </div>
      <DiffRow
        kind="Buttons"
        added={c.added_buttons}
        removed={c.removed_buttons}
      />
      <DiffRow kind="Links" added={addedLinks} removed={removedLinks} />
      <DiffRow kind="Nav items" added={addedNav} removed={removedNav} />
      <DiffRow
        kind="Form fields"
        added={c.added_form_fields}
        removed={c.removed_form_fields}
      />
    </div>
  )
}

function DiffRow({
  kind,
  added,
  removed,
}: {
  kind: string
  added: string[]
  removed: string[]
}) {
  if (!added.length && !removed.length) return null
  return (
    <div>
      <div className="text-[0.6rem] uppercase tracking-wide text-subtle-foreground mb-0.5">
        {kind}
      </div>
      <div className="flex flex-wrap gap-1">
        {removed.map((label) => (
          <span
            key={`r-${label}`}
            className="rounded bg-destructive-soft text-destructive-foreground px-2 py-0.5 text-[0.7rem] line-through"
          >
            − {label}
          </span>
        ))}
        {added.map((label) => (
          <span
            key={`a-${label}`}
            className="rounded bg-success-soft text-success-foreground px-2 py-0.5 text-[0.7rem]"
          >
            + {label}
          </span>
        ))}
      </div>
    </div>
  )
}

function BaselineDetail({
  baseline,
}: {
  baseline: NonNullable<DiagnosticFindings['baseline']>
}) {
  const hasInteractions = baseline.validated_interactions.length > 0
  const hasFields = baseline.validated_form_fields.length > 0
  const hasValidations = baseline.validated_validations.length > 0
  if (!hasInteractions && !hasFields && !hasValidations) {
    return (
      <div className="text-xs text-subtle-foreground italic">
        No validated patterns yet for this URL.
      </div>
    )
  }
  return (
    <div className="space-y-2 text-xs">
      <div className="text-[0.7rem] text-muted-foreground">
        Validated by passing tests on{' '}
        <code className="font-mono text-[0.65rem]">{baseline.url}</code>
      </div>
      {hasInteractions && (
        <TagList label="Interactions" items={baseline.validated_interactions} />
      )}
      {hasFields && (
        <TagList label="Form fields" items={baseline.validated_form_fields} />
      )}
      {hasValidations && (
        <TagList
          label="Validations"
          items={baseline.validated_validations}
        />
      )}
    </div>
  )
}

function TagList({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <div className="text-[0.6rem] uppercase tracking-wide text-subtle-foreground mb-0.5">
        {label}
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((it, i) => (
          <span
            key={i}
            className="rounded bg-success-soft/60 text-success-foreground px-2 py-0.5 text-[0.7rem]"
          >
            <CheckCircle className="inline h-2.5 w-2.5 mr-0.5 align-baseline" />
            {it}
          </span>
        ))}
      </div>
    </div>
  )
}
